// Builds the `errorRecoveryView` the diagnostics suppressor consumes,
// derived from the per-engine `buildParsedDocument` (`sql-grammar/normalize/`).
// GRA-7 Slice A3: this seam replaces the previous tree-sitter `Tree` that
// `applyDiagnosticsSuppression.ts` owned and walked — same WASM-load cost as
// before, routed through the model API instead of the IntelliSense parser
// pool. Mirrors the projection's view assembly in `editorContextProjection.ts`
// (`errorRanges` @ 1.0 + `missingRanges` @ 0.5).
//
// Extracted as its own module so the diagnostics-wrapper test can mock the
// builder without juggling 5 per-engine `vi.mock` calls.

import type { DatabaseEngineId } from '@/core/engines';
import { buildParsedDocument as buildMssqlDocument } from '@/features/sql-grammar/normalize/mssql';
import { buildParsedDocument as buildMysqlDocument } from '@/features/sql-grammar/normalize/mysql';
import { buildParsedDocument as buildOracleDocument } from '@/features/sql-grammar/normalize/oracle';
import { buildParsedDocument as buildPostgresDocument } from '@/features/sql-grammar/normalize/postgres';
import { buildParsedDocument as buildSqliteDocument } from '@/features/sql-grammar/normalize/sqlite';

import type { ErrorRecoveryEntry } from './suppressor';

/**
 * Compute the `errorRecoveryView` for `source` under `engineId`. Returns:
 *   • An array of `{range, confidence}` entries when the engine has a
 *     grammar fork and the parse succeeded. Empty array means "clean parse,
 *     no errors" — the safety-rail's confident-bind check still runs.
 *   • `null` when the engine has no grammar (`mock`) or the parse threw —
 *     the suppressor treats this as "no R3 signal available" and falls back
 *     to R1/R2 source-text rules + the safety rail's snapshot check.
 */
export async function buildErrorRecoveryView(
  engineId: DatabaseEngineId,
  source: string,
): Promise<ReadonlyArray<ErrorRecoveryEntry> | null> {
  try {
    switch (engineId) {
      case 'mssql':
        return entriesFromRecovery(
          await buildMssqlDocument(source, { engine: 'mssql' }),
        );
      case 'pgsql':
        return entriesFromRecovery(
          await buildPostgresDocument(source, { engine: 'postgres' }),
        );
      case 'mysql':
        return entriesFromRecovery(
          await buildMysqlDocument(source, { engine: 'mysql' }),
        );
      case 'oracle':
        return entriesFromRecovery(
          await buildOracleDocument(source, { engine: 'oracle' }),
        );
      case 'sqlite':
        return entriesFromRecovery(
          await buildSqliteDocument(source, { engine: 'sqlite' }),
        );
      case 'mock':
      case 'kusto':
      case 'azurestorage':
        return null;
    }
  } catch {
    return null;
  }
}

type DocLike = {
  readonly recovery: {
    readonly errorRanges: ReadonlyArray<ErrorRecoveryEntry['range']>;
    readonly missingRanges: ReadonlyArray<ErrorRecoveryEntry['range']>;
  };
};

function entriesFromRecovery(doc: DocLike): ReadonlyArray<ErrorRecoveryEntry> {
  return [
    ...doc.recovery.errorRanges.map((range) => ({ range, confidence: 1.0 })),
    ...doc.recovery.missingRanges.map((range) => ({ range, confidence: 0.5 })),
  ];
}
